import React, { useState, useEffect } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { EditorState, ContentState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import { stateToHTML } from 'draft-js-export-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import styles from './EditCategoryModal.module.css';

// Sanitize slug: lowercase, replace spaces/special chars with hyphens
function sanitizeSlug(value) {
    return value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function EditCategoryModal({ setOpenModal, data, setRefresh }) {
    const uid = localStorage.getItem('id');
    const [category, setCategory] = useState(data.name || '');
    const [message, setMessage] = useState(data.message || '');
    const [loading, setLoading] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [imageUrl, setImageUrl] = useState(data.imgUrl || '');
    const [status, setStatus] = useState(data.status || 0);
    const [activeTab, setActiveTab] = useState('basic');

    // SEO Fields
    const [metaTitle, setMetaTitle] = useState(data.metaTitle || '');
    const [metaDescription, setMetaDescription] = useState(data.metaDescription || '');
    const [slug, setSlug] = useState(data.slug ? sanitizeSlug(data.slug) : '');
    const [slugTouched, setSlugTouched] = useState(!!data.slug);
    const [focusKeywords, setFocusKeywords] = useState(
        Array.isArray(data.focusKeywords)
            ? data.focusKeywords
            : (typeof data.focusKeywords === 'string' && data.focusKeywords.trim()
                ? data.focusKeywords.split(',').map(s => s.trim()).filter(Boolean)
                : [])
    );
    const [keywordInput, setKeywordInput] = useState('');
    const [categoryDescription, setCategoryDescription] = useState(data.categoryDescription || '');
    const [categoryTagline, setCategoryTagline] = useState(data.categoryTagline || '');
    const [ogImage, setOgImage] = useState(data.ogImage || '');
    const [ogImageAlt, setOgImageAlt] = useState(data.ogImageAlt || '');
    const [noIndex, setNoIndex] = useState(data.noIndex || false);
    const [canonicalUrl, setCanonicalUrl] = useState(data.canonicalUrl || '');

    // Article Fields
    const [articleEnabled, setArticleEnabled] = useState(data.categoryArticle?.enabled || false);
    const [articleTitle, setArticleTitle] = useState(data.categoryArticle?.title || '');
    const [articleContent, setArticleContent] = useState(data.categoryArticle?.content || '');
    const [editorState, setEditorState] = useState(() => {
        const existingContent = data.categoryArticle?.content || '';
        if (existingContent) {
            const contentBlock = htmlToDraft(existingContent);
            if (contentBlock) {
                const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks, contentBlock.entityMap);
                return EditorState.createWithContent(contentState);
            }
        }
        return EditorState.createEmpty();
    });
    const [articlePosition, setArticlePosition] = useState(data.categoryArticle?.position || 'below_products');

    // Parent category selector
    const [parentId, setParentId] = useState(data.parentId ? data.parentId.toString() : '');
    const [parentOptions, setParentOptions] = useState([]);

    useEffect(() => {
        // Fetch category tree to populate parent selector
        // Flatten tree into options, exclude self and own descendants
        const selfId = data._id?.toString();
        function flattenForParent(nodes, depth = 0) {
            const result = [];
            for (const n of nodes) {
                const nId = n._id?.toString();
                if (nId === selfId) continue; // skip self
                result.push({ _id: nId, name: n.name, level: n.level || 1, _depth: depth });
                if (n.children && n.children.length > 0) {
                    result.push(...flattenForParent(n.children, depth + 1));
                }
            }
            return result;
        }
        import('../AxiosUrl').then(({ default: AxiosUrl }) => {
            AxiosUrl.get(`/category-tree/${uid}`)
                .then(res => {
                    if (res.data.success) {
                        setParentOptions(flattenForParent(res.data.data || []));
                    }
                })
                .catch(() => {});
        });
    }, [uid, data._id]);

    // Editor change handler
    const handleEditorChange = (newEditorState) => {
        setEditorState(newEditorState);
        const html = stateToHTML(newEditorState.getCurrentContent());
        setArticleContent(html === '<p><br></p>' ? '' : html);
    };

    // FAQ Fields
    const [faqs, setFaqs] = useState(
        Array.isArray(data.faqs) && data.faqs.length > 0
            ? data.faqs.map(f => ({ question: f.question || '', answer: f.answer || '', gid: f.gid || Date.now().toString() }))
            : []
    );

    const handleClose = () => setOpenModal(false);

    const [aiLoading, setAiLoading] = useState(false);
    const [showAiPrompt, setShowAiPrompt] = useState(false);
    const [aiAbout, setAiAbout] = useState(data.categoryDescription || data.message || '');
    const [aiProducts, setAiProducts] = useState(
        (Array.isArray(data.focusKeywords) ? data.focusKeywords.join(', ') : (data.focusKeywords || ''))
        || data.categoryTagline || ''
    );
    const [aiSection, setAiSection] = useState('all');

    const startAIGenerate = () => {
        if (!category.trim()) { toast.error('Enter category name first'); return; }
        setShowAiPrompt(true);
    };

    const handleAIGenerate = async () => {
        setShowAiPrompt(false);
        setAiLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetch('https://api.ecomtechbd.com/category/ai-generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', token, uid },
                body: JSON.stringify({ categoryName: category.trim(), aboutCategory: aiAbout.trim(), productTypes: aiProducts.trim(), section: aiSection }),
            });
            if (!res.ok) throw new Error('Server error');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    try {
                        const d = JSON.parse(line.slice(6));
                        if (d.type === 'complete' && d.success) {
                            const r = d.data;
                            const sec = aiSection;
                            if ((sec === 'all' || sec === 'seo') && r.metaTitle) setMetaTitle(r.metaTitle);
                            if ((sec === 'all' || sec === 'seo') && r.metaDescription) setMetaDescription(r.metaDescription);
                            if ((sec === 'all' || sec === 'seo') && r.categoryTagline) setCategoryTagline(r.categoryTagline);
                            if ((sec === 'all' || sec === 'seo') && r.categoryDescription) setCategoryDescription(r.categoryDescription);
                            if ((sec === 'all' || sec === 'seo') && r.focusKeywords?.length) setFocusKeywords(r.focusKeywords);
                            if ((sec === 'all' || sec === 'faqs') && r.faqs?.length) setFaqs(r.faqs.map((f, i) => ({ ...f, gid: String(Date.now() + i) })));
                            if ((sec === 'all' || sec === 'article') && r.articleTitle) { setArticleTitle(r.articleTitle); setArticleEnabled(true); }
                            if ((sec === 'all' || sec === 'article') && r.articleContent) {
                                setArticleContent(r.articleContent);
                                setArticleEnabled(true);
                                const blocks = htmlToDraft(r.articleContent);
                                if (blocks.contentBlocks) {
                                    const cs = ContentState.createFromBlockArray(blocks.contentBlocks, blocks.entityMap);
                                    setEditorState(EditorState.createWithContent(cs));
                                }
                            }
                            toast.success('AI content generated!');
                        } else if (d.type === 'complete' && !d.success) {
                            toast.error(d.message || 'Generation failed');
                        }
                    } catch {}
                }
            }
        } catch (err) {
            toast.error('Failed: ' + err.message);
        } finally {
            setAiLoading(false);
        }
    };

    // Auto-generate slug from category name if no slug exists
    useEffect(() => {
        if (category && !slugTouched) {
            setSlug(sanitizeSlug(category));
        }
    }, [category, slugTouched]);

    const handleAddKeyword = (event) => {
        if (event.key === 'Enter' && keywordInput.trim()) {
            event.preventDefault();
            if (!focusKeywords.includes(keywordInput.trim())) {
                setFocusKeywords([...focusKeywords, keywordInput.trim()]);
            }
            setKeywordInput('');
        }
    };

    const handleDeleteKeyword = (keywordToDelete) => {
        setFocusKeywords(focusKeywords.filter((keyword) => keyword !== keywordToDelete));
    };

    // FAQ handlers
    const addFaq = () => {
        setFaqs([...faqs, { question: '', answer: '', gid: Date.now().toString() }]);
    };

    const removeFaq = (index) => {
        setFaqs(faqs.filter((_, i) => i !== index));
    };

    const updateFaq = (index, field, value) => {
        const updated = [...faqs];
        updated[index] = { ...updated[index], [field]: value };
        setFaqs(updated);
    };

    const handleSave = () => {
        setLoading(true);
        if (!category) {
            toast.error('Category name is required');
            setLoading(false);
            return;
        }

        const formData = {
            uid,
            categoryName: category,
            imgUrl: imageUrl,
            oldCategory: data.name,
            message,
            status,
            parentId: parentId || null,
            metaTitle: metaTitle || category,
            metaDescription,
            slug: slug || sanitizeSlug(category),
            focusKeywords,
            categoryDescription,
            categoryTagline,
            ogImage,
            ogImageAlt,
            noIndex,
            canonicalUrl,
            categoryArticle: {
                enabled: articleEnabled,
                title: articleTitle,
                content: articleContent,
                position: articlePosition,
            },
            faqs: faqs.filter(f => f.question.trim() && f.answer.trim()),
        };

        AxiosUrl.post(`/edit-category/${uid}`, formData)
            .then((res) => {
                toast.success('Category updated successfully');
                setRefresh((prev) => prev + 1);
                handleClose();
                setLoading(false);
            })
            .catch((error) => {
                console.error('Failed to update category:', error);
                toast.error('Failed to update category');
                setLoading(false);
            });
    };

    const cdnImage = async () => {
        const image = new FormData();
        image.append('photo', photo);
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data',
                token: 'zesrxdyctuutydrszrestdrxy',
            },
        };
        const url = 'https://cdn.ecomtechbd.com/upload/category';
        axios
            .post(url, image, config)
            .then((res) => {
                toast.success('Image uploaded to CDN');
                setImageUrl(res.data);
            })
            .catch((error) => {
                console.error('Failed to upload image:', error);
                toast.error('Failed to upload image');
            });
    };

    useEffect(() => {
        if (photo) {
            cdnImage();
        }
    }, [photo]);

    const handleFileChange = (event) => {
        setPhoto(event.target.files[0]);
    };

    const renderBasicTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.imageUploadSection}>
                {imageUrl ? (
                    <div className={styles.imagePreviewContainer}>
                        <img src={imageUrl} alt="Category" className={styles.imagePreview} />
                        <label className={styles.reuploadOverlay}>
                            <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                            <CloudUploadIcon />
                            <span>Re-upload</span>
                        </label>
                    </div>
                ) : (
                    <label className={styles.uploadButton}>
                        <input hidden accept="image/*" type="file" onChange={handleFileChange} />
                        <CloudUploadIcon />
                        <span>Upload Image</span>
                    </label>
                )}
            </div>
            <div className={styles.formGroup}>
                <label>Category Name *</label>
                <input type="text" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Enter category name" />
            </div>
            <div className={styles.formGroup}>
                <label>Category Message (Optional)</label>
                <input type="text" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Optional message for this category" />
            </div>
            <div className={styles.formGroup}>
                <label>Status</label>
                <select value={status} onChange={(e) => setStatus(parseInt(e.target.value))}>
                    <option value={0}>Active</option>
                    <option value={1}>Inactive</option>
                </select>
            </div>
        </div>
    );

    const renderSEOTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.formGroup}>
                <label>SEO Title</label>
                <input type="text" value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} placeholder={category || 'Auto-generated from category name'} />
                <small className={`${styles.characterCounter} ${metaTitle.length > 60 ? styles.warning : ''}`}>{metaTitle.length}/60 characters</small>
            </div>
            <div className={styles.formGroup}>
                <label>Meta Description</label>
                <textarea value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} placeholder="Brief description for search engines" rows={3} />
                <small className={`${styles.characterCounter} ${metaDescription.length > 160 ? styles.warning : ''}`}>{metaDescription.length}/160 characters</small>
            </div>
            <div className={styles.formGroup}>
                <label>URL Slug</label>
                <input type="text" value={slug} onChange={(e) => { setSlugTouched(true); setSlug(sanitizeSlug(e.target.value)); }} placeholder="auto-generated-slug" />
                <small>URL-friendly slug for this category</small>
            </div>
            <div className={styles.formGroup}>
                <label>Focus Keywords</label>
                <input type="text" value={keywordInput} onChange={(e) => setKeywordInput(e.target.value)} onKeyDown={handleAddKeyword} placeholder="Type a keyword and press Enter" />
                <small>Press Enter to add each keyword</small>
            </div>
            {focusKeywords.length > 0 && (
                <div className={styles.keywordChipsContainer}>
                    {focusKeywords.map((keyword) => (
                        <div key={keyword} className={styles.keywordChip}>
                            <span>{keyword}</span>
                            <button onClick={() => handleDeleteKeyword(keyword)}>×</button>
                        </div>
                    ))}
                </div>
            )}
            <div className={styles.formGroup}>
                <label>Category Tagline</label>
                <input type="text" value={categoryTagline} onChange={(e) => setCategoryTagline(e.target.value)} placeholder="Short promotional tagline for the category" />
                <small>Displayed as a header on the category page</small>
            </div>
            <div className={styles.formGroup}>
                <label>Category Description</label>
                <textarea value={categoryDescription} onChange={(e) => setCategoryDescription(e.target.value)} placeholder="Long-form description for category page (300-500 words recommended)" rows={6} />
                <small>For SEO and content purposes</small>
            </div>

            <hr style={{ margin: '16px 0', borderColor: '#eee' }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>Open Graph / Advanced SEO</div>

            <div className={styles.formGroup}>
                <label>OG Image URL</label>
                <input type="text" value={ogImage} onChange={(e) => setOgImage(e.target.value)} placeholder="https://cdn.example.com/category-image.jpg" />
                <small>Custom image for Facebook/Twitter sharing (overrides default)</small>
            </div>
            <div className={styles.formGroup}>
                <label>OG Image Alt Text</label>
                <input type="text" value={ogImageAlt} onChange={(e) => setOgImageAlt(e.target.value)} placeholder="Descriptive alt text for the OG image" />
                <small>Improves accessibility and social share context</small>
            </div>
            <div className={styles.formGroup}>
                <label>Canonical URL</label>
                <input type="text" value={canonicalUrl} onChange={(e) => setCanonicalUrl(e.target.value)} placeholder="https://yourstore.com/category/slug" />
                <small>Override the canonical URL for this category page</small>
            </div>
            <div className={styles.toggleRow} style={{ marginTop: 8 }}>
                <label>No Index (hide from search engines)</label>
                <button type="button" className={`${styles.toggleSwitch} ${noIndex ? styles.active : ''}`} onClick={() => setNoIndex(!noIndex)} />
            </div>
            {noIndex && (
                <div style={{ fontSize: 12, color: '#e53935', marginTop: 4 }}>
                    <FiAlertTriangle style={{ verticalAlign: 'middle' }} /> This page will be excluded from search engine indexing.
                </div>
            )}
        </div>
    );

    const renderArticleTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionInfo}>
                Add an SEO article to the category page. This helps improve search rankings with rich, keyword-optimized content.
            </div>
            <div className={styles.toggleRow}>
                <label>Enable Article</label>
                <button type="button" className={`${styles.toggleSwitch} ${articleEnabled ? styles.active : ''}`} onClick={() => setArticleEnabled(!articleEnabled)} />
            </div>
            {articleEnabled && (
                <>
                    <div className={styles.formGroup}>
                        <label>Article Title</label>
                        <input type="text" value={articleTitle} onChange={(e) => setArticleTitle(e.target.value)} placeholder="e.g., About Our Skincare Collection" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Article Content</label>
                        <div className={styles.editorWrapper}>
                            <Editor
                                editorState={editorState}
                                onEditorStateChange={handleEditorChange}
                                wrapperClassName={styles.editorWrapperInner}
                                editorClassName={styles.editorContent}
                                toolbarClassName={styles.editorToolbar}
                                placeholder="Write your article content here..."
                                toolbar={{
                                    options: ['inline', 'blockType', 'list', 'textAlign', 'link', 'history'],
                                    inline: { inDropdown: false, options: ['bold', 'italic', 'underline', 'strikethrough'] },
                                    blockType: { inDropdown: true, options: ['Normal', 'H2', 'H3', 'H4', 'Blockquote'] },
                                    list: { inDropdown: false, options: ['unordered', 'ordered'] },
                                    textAlign: { inDropdown: true },
                                    link: { inDropdown: false, showOpenOptionOnHover: true, defaultTargetOption: '_blank' },
                                    history: { inDropdown: false, options: ['undo', 'redo'] },
                                }}
                            />
                        </div>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Article Position</label>
                        <select value={articlePosition} onChange={(e) => setArticlePosition(e.target.value)}>
                            <option value="below_products">Below Products (Default)</option>
                            <option value="above_products">Above Products</option>
                        </select>
                    </div>
                </>
            )}
        </div>
    );

    const renderFAQsTab = () => (
        <div className={styles.tabContent}>
            <div className={styles.sectionInfo}>
                Add FAQs to generate FAQ Schema markup (structured data) for better SEO. These appear as an accordion on the category page.
            </div>
            {faqs.length === 0 && (
                <div className={styles.emptyFaqMessage}>No FAQs added yet. Click the button below to add your first FAQ.</div>
            )}
            {faqs.map((faq, index) => (
                <div key={faq.gid} className={styles.faqItem}>
                    <div className={styles.faqItemHeader}>
                        <span>FAQ #{index + 1}</span>
                        <button type="button" className={styles.removeFaqBtn} onClick={() => removeFaq(index)}>×</button>
                    </div>
                    <div className={styles.formGroup}>
                        <label>Question</label>
                        <input type="text" value={faq.question} onChange={(e) => updateFaq(index, 'question', e.target.value)} placeholder="Enter the FAQ question" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Answer</label>
                        <textarea value={faq.answer} onChange={(e) => updateFaq(index, 'answer', e.target.value)} placeholder="Enter the answer" rows={3} />
                    </div>
                </div>
            ))}
            <button type="button" className={styles.addFaqBtn} onClick={addFaq}>+ Add FAQ</button>
        </div>
    );

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && handleClose()}>
            <ToastContainer />
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Edit Category</h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button type="button" onClick={startAIGenerate} disabled={aiLoading}
                            style={{ padding: '6px 14px', background: aiLoading ? '#e8e8ee' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: aiLoading ? '#6d7175' : '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: aiLoading ? 'not-allowed' : 'pointer' }}>
                            {aiLoading ? 'Generating...' : 'AI Generate'}
                        </button>
                        <button className={styles.closeButton} onClick={handleClose}>×</button>
                    </div>
                </div>
                {showAiPrompt && (
                    <div style={{ padding: '16px 24px', background: '#f0f0ff', borderBottom: '1px solid #e1e3e5' }}>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#202223', marginBottom: '10px' }}>Tell AI about this category:</p>
                        <input type="text" placeholder="What is this category about?" value={aiAbout} onChange={e => setAiAbout(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }} />
                        <input type="text" placeholder="Products in this category?" value={aiProducts} onChange={e => setAiProducts(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }} />
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
                            <select value={aiSection} onChange={e => setAiSection(e.target.value)}
                                style={{ padding: '6px 10px', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '12px', background: '#fff' }}>
                                <option value="all">Regenerate All</option>
                                <option value="seo">SEO Only</option>
                                <option value="article">Article Only</option>
                                <option value="faqs">FAQs Only</option>
                            </select>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={() => setShowAiPrompt(false)} style={{ padding: '6px 14px', background: '#fff', border: '1px solid #c9cccf', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                                <button onClick={handleAIGenerate} style={{ padding: '6px 14px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Generate</button>
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.tabs}>
                    <button className={activeTab === 'basic' ? styles.activeTab : ''} onClick={() => setActiveTab('basic')}>Basic Info</button>
                    <button className={activeTab === 'seo' ? styles.activeTab : ''} onClick={() => setActiveTab('seo')}>SEO Settings</button>
                    <button className={activeTab === 'article' ? styles.activeTab : ''} onClick={() => setActiveTab('article')}>Article</button>
                    <button className={activeTab === 'faqs' ? styles.activeTab : ''} onClick={() => setActiveTab('faqs')}>FAQs</button>
                </div>
                {activeTab === 'basic' && renderBasicTab()}
                {activeTab === 'seo' && renderSEOTab()}
                {activeTab === 'article' && renderArticleTab()}
                {activeTab === 'faqs' && renderFAQsTab()}
                <div className={styles.modalFooter}>
                    <button className={styles.cancelButton} onClick={handleClose}>Cancel</button>
                    <button className={styles.submitButton} onClick={handleSave} disabled={loading}>{loading ? 'Updating...' : 'Update Category'}</button>
                </div>
            </div>
        </div>
    );
}

export default EditCategoryModal;
